import React, { useEffect, useRef, useState } from "react";
import styles from "./ReportConfirmModal.module.css";
import { reactIcons } from "../../../icons/reactIcons";
import MapView from "@arcgis/core/views/MapView";
import WebMap from "@arcgis/core/WebMap";
import Graphic from "@arcgis/core/Graphic";
import SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol";
import Point from "@arcgis/core/geometry/Point";
import { reverseGeocode } from "../../../utils/reverseGeocode";
import { AddressSearchResult, homeApis } from "../../../apis/homeApis";
import { useDispatch } from "react-redux";
import { showSnackbar } from "../../../redux-store/confirmSnackbarSlice";

interface ReportConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (location: {
    lat: number;
    lng: number;
    address?: string;
    city?: string;
    addressDetails: AddressSearchResult;
  }) => void;
}

const ReportConfirmModal: React.FC<ReportConfirmModalProps> = ({
  open,
  onClose,
  onConfirm,
}) => {
  const dispatch = useDispatch();
  const [locationMode, setLocationMode] = useState<"current" | "search">(
    "current",
  );
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(
    null,
  );
  const [address, setAddress] = useState<string>("");
  const [city, setCity] = useState<string>("");
  const [streetName, setStreetName] = useState("");
  const [streetStart, setStreetStart] = useState("");
  const [searchResults, setSearchResults] = useState<AddressSearchResult[]>([]);
  const [selectedAddress, setSelectedAddress] =
    useState<AddressSearchResult | null>(null);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentLocationNoMatch, setCurrentLocationNoMatch] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<MapView | null>(null);

  const notify = (message: string) => {
    dispatch(showSnackbar({ message, type: "error", autoCloseDuration: 4000 }));
  };

  const getApiMessage = (error: any, fallback: string) =>
    error?.response?.data?.message || error?.message || fallback;

  const resetFields = () => {
    setLocationMode("current");
    setCoords(null);
    setAddress("");
    setCity("");
    setStreetName("");
    setStreetStart("");
    setSearchResults([]);
    setSelectedAddress(null);
    setSearching(false);
    setLoading(false);
    setCurrentLocationNoMatch(false);
  };

  //  Detect location automatically
  const detectLocation = async () => {
    setLoading(true);

    try {
      if (!navigator.geolocation) {
        setCurrentLocationNoMatch(true);
        notify("Geolocation is not supported by your browser");
        setLoading(false);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const lat = pos.coords.latitude;
          const lng = pos.coords.longitude;

          // const lat = 38.24503;
          // const lng = -85.74336;

          setCoords({ lat, lng });

          // Fetch address using reverseGeocode utility
          const addr = await reverseGeocode(lat, lng);
          setAddress(addr.address);
          setCity(addr.city);

          if (addr.streetName || addr.streetStart) {
            await searchAddresses(
              {
                streetName: addr.streetName,
                streetStart: addr.streetStart,
              },
              true,
            );
          } else {
            setCurrentLocationNoMatch(true);
            notify("No matching address found. Search the address manually.");
          }

          setLoading(false);
        },
        () => {
          setCurrentLocationNoMatch(true);
          notify("Unable to retrieve your current location");
          setLoading(false);
        },
      );
    } catch (error) {
      console.error("Location error:", error);
      setCurrentLocationNoMatch(true);
      notify("Unable to fetch your current address");
    } finally {
      setLoading(false);
    }
  };

  // Auto detect location when modal opens
  useEffect(() => {
    if (open) {
      resetFields();
      detectLocation();
    }
  }, [open]);

  const searchAddresses = async (
    searchParams?: {
      streetName?: string;
      streetStart?: string;
    },
    forCurrentLocation = false,
  ) => {
    const nextStreetName = searchParams?.streetName ?? streetName.trim();
    const nextStreetStart = searchParams?.streetStart ?? streetStart.trim();

    if (!nextStreetName && !nextStreetStart) {
      notify("Enter a street name or street number");
      return;
    }

    setSearching(true);
    setSearchResults([]);
    setSelectedAddress(null);

    try {
      const results = await homeApis.searchAddresses({
        ...(nextStreetName ? { streetName: nextStreetName } : {}),
        ...(nextStreetStart ? { streetStart: nextStreetStart } : {}),
      });
      setSearchResults(results);
      if (!results.length) {
        if (forCurrentLocation) {
          setCurrentLocationNoMatch(true);
        }
        notify("No matching address found. Search the address manually.");
      } else if (forCurrentLocation) {
        setCurrentLocationNoMatch(false);
      }
    } catch (error) {
      console.error("Address search error:", error);
      if (forCurrentLocation) {
        setCurrentLocationNoMatch(true);
      }
      notify(getApiMessage(error, "Unable to search addresses"));
    } finally {
      setSearching(false);
    }
  };

  const selectAddress = (result: AddressSearchResult) => {
    const selectedAddressText =
      result.addressLine1 || result.streetAddress || result.streetName;

    setSelectedAddress(result);
    setCoords({ lat: result.yCoordinate, lng: result.xCoordinate });
    setAddress(selectedAddressText);
    setCity(result.city);
    setSearchResults([]);
  };

  const handleCancel = () => {
    resetFields();
    onClose();
  };

  const handleManualSearch = () => {
    setLocationMode("search");
    setCoords(null);
    setAddress("");
    setCity("");
    setStreetName("");
    setStreetStart("");
    setSearchResults([]);
    setSelectedAddress(null);
    setCurrentLocationNoMatch(false);
  };

  const handleContinue = () => {
    if (!coords || !selectedAddress) return;

    onConfirm({
      ...coords,
      address,
      city,
      addressDetails: selectedAddress,
    });
    resetFields();
  };

  // Initialize ArcGIS Map
  useEffect(() => {
    if (!coords || !selectedAddress || !open) return;

    const webmap = new WebMap({
      basemap: "streets-navigation-vector",
    });

    const view = new MapView({
      container: mapRef.current as HTMLDivElement,
      map: webmap,
      center: [coords.lng, coords.lat],
      zoom: 16,
    });

    const point = new Point({
      longitude: coords.lng,
      latitude: coords.lat,
    });

    const markerSymbol = new SimpleMarkerSymbol({
      color: "#1a73e8",
      outline: {
        color: "#fff",
        width: 2,
      },
    });

    const pointGraphic = new Graphic({
      geometry: point,
      symbol: markerSymbol,
    });

    view.graphics.add(pointGraphic);
    viewRef.current = view;

    return () => {
      if (viewRef.current) {
        viewRef.current.destroy();
        viewRef.current.container = null;
      }
    };
  }, [coords, selectedAddress, open]);

  useEffect(() => {
    if (open) {
      document.body.classList.add("reportConfirm-modal-open");
    } else {
      document.body.classList.remove("reportConfirm-modal-open");
    }

    return () => document.body.classList.remove("reportConfirm-modal-open");
  }, [open]);

  if (!open) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <h3 className={styles.title}>Confirm Your Location</h3>
        <p className={styles.subtitle}>
          Choose your current location or search for an address.
        </p>

        <div className={styles.modeSwitch}>
          <button
            type="button"
            className={`${styles.modeButton} ${
              locationMode === "current" ? styles.activeModeButton : ""
            }`}
            onClick={() => {
              setLocationMode("current");
              setCoords(null);
              setAddress("");
              setCity("");
              setStreetName("");
              setStreetStart("");
              setSearchResults([]);
              setSelectedAddress(null);
              setCurrentLocationNoMatch(false);
              detectLocation();
            }}
          >
            Current location
          </button>
          <button
            type="button"
            className={`${styles.modeButton} ${
              locationMode === "search" ? styles.activeModeButton : ""
            }`}
            onClick={() => {
              setLocationMode("search");
              setCoords(null);
              setAddress("");
              setSearchResults([]);
              setSelectedAddress(null);
            }}
          >
            Search address
          </button>
        </div>

        {locationMode === "search" && (
          <div className={styles.searchBox}>
            <div className={styles.searchFields}>
              <input
                value={streetName}
                onChange={(event) => setStreetName(event.target.value)}
                placeholder="Street name"
                className={styles.searchInput}
              />
              <input
                value={streetStart}
                onChange={(event) => setStreetStart(event.target.value)}
                placeholder="Street number"
                inputMode="numeric"
                className={styles.searchInput}
              />
              <button
                type="button"
                onClick={() => searchAddresses()}
                disabled={searching}
                className={styles.searchButton}
              >
                {searching ? "Searching..." : "Search"}
              </button>
            </div>

            {searchResults.length > 0 && (
              <div className={styles.searchResults}>
                {searchResults.map((result, index) => (
                  <button
                    type="button"
                    key={
                      result.id ??
                      `${result.streetStart}-${result.xCoordinate}-${result.yCoordinate}-${index}`
                    }
                    className={`${styles.resultButton} ${
                      selectedAddress?.id === result.id
                        ? styles.selectedResult
                        : ""
                    }`}
                    onClick={() => selectAddress(result)}
                  >
                    <span>
                      {result.addressLine1 ||
                        result.streetAddress ||
                        result.streetName}
                    </span>
                    <small>
                      {result.city}, {result.state?.value || ""}{" "}
                      {result.postalCode || ""}
                    </small>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {locationMode === "current" && searchResults.length > 0 && (
          <div className={styles.currentResultsBox}>
            <div className={styles.searchResults}>
              {searchResults.map((result, index) => (
                <button
                  type="button"
                  key={
                    result.id ??
                    `${result.streetStart}-${result.xCoordinate}-${result.yCoordinate}-${index}`
                  }
                  className={styles.resultButton}
                  onClick={() => selectAddress(result)}
                >
                  <span>
                    {result.addressLine1 ||
                      result.streetAddress ||
                      result.streetName}
                  </span>
                  <small>
                    {result.city}, {result.state?.value || ""}{" "}
                    {result.postalCode || ""}
                  </small>
                </button>
              ))}
            </div>
          </div>
        )}

        {(locationMode === "current" || selectedAddress) && (
          <div className={styles.locationCard}>
            <div className={styles.locationHeader}>
              <span>
                {locationMode === "current"
                  ? "Your Current Location"
                  : "Selected Address"}
              </span>
            </div>

            {selectedAddress && coords ? (
              <>
                <div className={styles.address}>{address}</div>
                <div className={styles.coords}>
                  {coords.lat.toFixed(4)}° N, {coords.lng.toFixed(4)}° E
                </div>

                <div ref={mapRef} className={styles.mapContainer}></div>
              </>
            ) : locationMode === "current" && currentLocationNoMatch ? (
              <div className={styles.noMatchMessage}>
                <p>No matched location found.</p>
                <span>Please search location manually.</span>
                <button type="button" onClick={handleManualSearch}>
                  Search location
                </button>
              </div>
            ) : (
              <div className={styles.placeholder}>
                <p>
                  {loading
                    ? "Finding a matching location..."
                    : "Searching for a matching location..."}
                </p>
              </div>
            )}
          </div>
        )}

        <div className={styles.actions}>
          <button onClick={handleCancel} className={styles.cancelBtn}>
            Cancel
          </button>
          <button
            onClick={handleContinue}
            className={styles.continueBtn}
            disabled={!coords || !selectedAddress || loading || searching}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportConfirmModal;
